import { Component, useState, useEffect, useRef } from 'react';
import SakuraOrbit from './SakuraOrbit';
import PageMeta from './PageMeta';
import { Link } from 'react-router-dom';
import SakuraSep from './SakuraSep';
import Crown from './Crown';
import { DataRPC } from '../../rpc-client/DataRPC';
import { s3 } from '../../config/constants';

const rpc = new DataRPC();

/* ── Icons ────────────────────────────────────────────────── */
const IgIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
);
const MailIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
    </svg>
);
const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
    </svg>
);
const FbIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
    </svg>
);
const CameraIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
        <circle cx="12" cy="13" r="4"/>
    </svg>
);

/* ── Palette ──────────────────────────────────────────────── */
const P = [
    ['#e12454', '#223a66'], ['#2c5f7a', '#223a66'],
    ['#e85d04', '#c9932a'], ['#6b3f8c', '#223a66'],
    ['#1a7a6b', '#2c5f7a'], ['#e12454', '#6b3f8c'],
    ['#c9932a', '#e12454'], ['#223a66', '#1a7a6b'],
];
const grad = (i) => `linear-gradient(135deg, ${P[i % P.length][0]} 0%, ${P[i % P.length][1]} 100%)`;

/* ── Data ─────────────────────────────────────────────────── */

const fmtDate = (dt) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};




const RANK_STYLES = [
    { bg: 'linear-gradient(135deg, #ffd700 0%, #f0a500 100%)', color: '#7a5000' },
    { bg: 'linear-gradient(135deg, #e0e0e0 0%, #b0b0b0 100%)', color: '#555'   },
    { bg: 'linear-gradient(135deg, #e8a96a 0%, #cd7f32 100%)', color: '#6b3c10' },
];

const initials = (name) => name.trim().split(/\s+/).map(w => w[0]).slice(-2).join('').toUpperCase();
const igHandle = (url) => { if (!url) return null; const m = url.match(/instagram\.com\/([^/?#]+)/); return m ? `@${m[1]}` : null; };

/* ── SearchableSelect ─────────────────────────────────────── */
function SearchableSelect({ value, onChange, options, placeholder, loading }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    const visible = options.filter(o =>
        !query || o.label.toLowerCase().includes(query.toLowerCase())
    );
    const selectedLabel = value === 'all' ? '' : (options.find(o => o.value === value)?.label ?? '');

    const handleOpen = () => {
        if (loading) return;
        setOpen(true);
        setQuery('');
        setTimeout(() => inputRef.current && inputRef.current.focus(), 10);
    };

    const handleSelect = (val) => {
        onChange(val);
        setOpen(false);
        setQuery('');
    };

    return (
        <div className="gal-ss" ref={wrapRef}>
            <div className={`gal-ss-trigger${open ? ' open' : ''}`} onClick={!open ? handleOpen : undefined}>
                <input
                    ref={inputRef}
                    className="gal-ss-input"
                    value={open ? query : selectedLabel}
                    placeholder={open ? 'Tìm kiếm...' : (loading ? 'Đang tải...' : placeholder)}
                    onChange={e => setQuery(e.target.value)}
                    disabled={loading}
                />
                <span className="gal-select-arrow" />
            </div>
            {open && (
                <div className="gal-ss-menu">
                    <div
                        className={`gal-ss-option${value === 'all' ? ' active' : ''}`}
                        onMouseDown={() => handleSelect('all')}
                    >{placeholder}</div>
                    {visible.map(o => (
                        <div
                            key={o.value}
                            className={`gal-ss-option${o.value === value ? ' active' : ''}`}
                            onMouseDown={() => handleSelect(o.value)}
                        >{o.label}</div>
                    ))}
                    {visible.length === 0 && <div className="gal-ss-empty">Không tìm thấy</div>}
                </div>
            )}
        </div>
    );
}

/* ── Component ────────────────────────────────────────────── */
class Gallery extends Component {
    state = { filterFest: 'all', filterYear: '2026', filterCameraman: 'all', filterTeam: 'all', cameramen: [], cameramenLoading: true, cameramenById: {}, festivals: [], festivalsLoading: false, teams: [], teamsLoading: false, albums: [], albumsLoading: true, teamRanking: [], rankingLoading: true, activeSection: 'gal-sec-photog', albumPage: 1 };

    ALBUMS_PER_PAGE = 6;

    setAlbumPage(p) {
        // Keep the view anchored to the top of the album section on page change,
        // instead of letting the grid height difference shift the scroll position.
        this.setState({ albumPage: p }, () => {
            const el = document.getElementById('gal-sec-albums');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    scrollToSection(id) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    loadFestivals(year) {
        this.setState({ festivalsLoading: true, filterFest: 'all' });
        rpc.fetchRawData('GetFestivals', { page: 1, limit: 100 })
            .then(res => {
                const list = year === 'all'
                    ? (res.data || [])
                    : (res.data || []).filter(f => f.start_date && String(f.start_date).startsWith(year));
                this.setState({ festivals: list, festivalsLoading: false });
            })
            .catch(() => this.setState({ festivalsLoading: false }));
    }

    loadTeams() {
        this.setState({ teamsLoading: true });
        rpc.fetchRawData('GetTeams', { page: 1, limit: 100 })
            .then(res => this.setState({ teams: res.data || [], teamsLoading: false }))
            .catch(() => this.setState({ teamsLoading: false }));
    }

    componentDidMount() {
        rpc.fetchRawData('GetCameramen', { page: 1, limit: 50 })
            .then(res => {
                const raw = res.data || [];
                const cameramenById = {};
                raw.forEach(c => { cameramenById[c.id] = c.name; });
                const list = raw.map((c, i) => ({
                    id:       c.id,
                    name:     c.name,
                    title:    c.introduction || c.style || '',
                    initials: initials(c.name),
                    ig:         igHandle(c.instagram_url),
                    email:      c.email || null,
                    phone:      c.phone || null,
                    facebook:   c.facebook_url || null,
                    avatar_url: c.avatar_url || null,
                    gradient:   grad(i),
                }));
                this.setState({ cameramen: list, cameramenLoading: false, cameramenById });
            })
            .catch(() => this.setState({ cameramenLoading: false }));
        rpc.fetchRawData('GetAlbums', { page: 1, limit: 500 })
            .then(res => this.setState({ albums: res.data || [], albumsLoading: false }))
            .catch(() => this.setState({ albumsLoading: false }));
        this.loadFestivals('2026');
        this.loadTeams();
        rpc.fetchRawData('GetTeamRanking', { limit: 10 })
            .then(res => this.setState({ teamRanking: Array.isArray(res) ? res : [], rankingLoading: false }))
            .catch(() => this.setState({ rankingLoading: false }));

    }

    componentWillUnmount() {
        if (this._sectionObserver) this._sectionObserver.disconnect();
    }

    componentDidUpdate(_, prevState) {
        if (prevState.filterYear !== this.state.filterYear) {
            this.loadFestivals(this.state.filterYear);
        }
        // Reset to the first album page whenever a filter narrows the list.
        if (prevState.filterFest !== this.state.filterFest
            || prevState.filterYear !== this.state.filterYear
            || prevState.filterCameraman !== this.state.filterCameraman) {
            if (this.state.albumPage !== 1) this.setState({ albumPage: 1 });
        }
        if (!this._sectionObserver) {
            this._sectionObserver = new IntersectionObserver(
                entries => entries.forEach(e => { if (e.isIntersecting) this.setState({ activeSection: e.target.id }); }),
                { rootMargin: '-70px 0px -55% 0px', threshold: 0 }
            );
            ['gal-sec-photog', 'gal-sec-albums', 'gal-sec-ranking'].forEach(id => {
                const el = document.getElementById(id);
                if (el) this._sectionObserver.observe(el);
            });
        }
    }

    render() {
        const { filterFest, filterYear, filterCameraman, filterTeam, cameramen, cameramenLoading, cameramenById, festivals, festivalsLoading, albums, albumsLoading, teamRanking, rankingLoading } = this.state;
        const maxPics = teamRanking.length > 0 ? teamRanking[0].pic_count : 1;

        const filtered = albums.filter(a =>
            (filterYear === 'all' || String(a.take_in_year) === filterYear)
            && (filterFest === 'all' || String(a.festival_id) === filterFest)
            && (filterCameraman === 'all' || String(a.cameraman_id) === filterCameraman)
        );

        // Album pagination — 6 per page.
        const albumTotalPages = Math.max(1, Math.ceil(filtered.length / this.ALBUMS_PER_PAGE));
        const albumPage = Math.min(this.state.albumPage, albumTotalPages);
        const pagedAlbums = filtered.slice((albumPage - 1) * this.ALBUMS_PER_PAGE, albumPage * this.ALBUMS_PER_PAGE);

        return (
            <div className="gallery-pg">
                <PageMeta title="Gallery" description="Album ảnh lễ hội Yosakoi – những khoảnh khắc đẹp nhất của Vietnam Yosakoi Festival." url="/gallery" image="https://vietnamyosakoi.com/images/blog-page-background.jpg" />

                {/* Page header */}
                <div className="page-header gallery-pg-header">
                    <div className="page-header-overlay" />
                    <div className="container page-header-body">
                        <div className="row">
                            <div className="col-12">
                                <div className="entry-meta-date">Việt Nam Yosakoi Festival</div>
                                <h1 className="page-header-title">ẢNH LỄ HỘI</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sep-gold-rule" style={{margin: 0}} />

                <div className="tmd-section-nav">
                    <div className="container">
                        <div className="tmd-section-nav-inner">
                            <div className="tmd-sec-nav-btn-group">
                            {[
                                { id: 'gal-sec-photog', label: 'Nhiếp Ảnh Gia' },
                                { id: 'gal-sec-albums', label: 'Album Ảnh' },
                                { id: 'gal-sec-ranking', label: 'Xếp Hạng Tag' },
                            ].map(({ id, label }) => (
                                <div key={id} className={`tmd-nav-item${this.state.activeSection === id ? ' active' : ''}`}>
                                    <button
                                        className="tmd-sec-nav-btn"
                                        onClick={() => this.scrollToSection(id)}
                                    ><span className="tmd-btn-bg" /></button>
                                    <span className="tmd-btn-label">{label}</span>
                                </div>
                            ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="gallery-pg-body">

                    {/* ── Section 1: Photographers ── */}
                    <div id="gal-sec-photog" className="gallery-section gallery-section-photographers">
                        <div className="container">
                            <div className="entry-title">
                                <p>ĐỘI NGŨ CAMERAMEN</p>
                                <h2> NHIẾP ẢNH GIA YOSAKOI</h2>
                            </div>
                            {cameramenLoading && <SakuraOrbit size="sm" label="Đang tải" />}
                        </div>
                        {!cameramenLoading && (() => {
                            if (cameramen.length === 0) return null;
                            // Each half-track must be wider than the viewport for seamless -50% scroll.
                            // Card = 240px + 20px gap = 260px. At 1920px we need ≥ 8 cards per half.
                            // Generate enough repeats so each row has ≥ 12 cards before duplication.
                            const minPerRow = 12;
                            const src = cameramen;
                            const makeRow = (offset) => Array.from(
                                { length: Math.max(minPerRow, Math.ceil(src.length / 2) * 3) },
                                (_, i) => src[((i * 2) + offset) % src.length]
                            );
                            const row1 = makeRow(0);
                            const row2 = makeRow(1);
                            const track1 = [...row1, ...row1];
                            const track2 = [...row2, ...row2];
                            const renderCard = (p, i) => (
                                <div key={i} className="gal-photog-item">
                                    <div className="gal-photog-card">
                                        <div className="gal-photog-top" style={{ background: p.gradient }} />
                                        <div className="gal-photog-avatar" style={{ background: p.gradient }}>
                                            {p.avatar_url
                                                ? <img src={s3(p.avatar_url, '')} alt={p.name} className="gal-photog-avatar-img" />
                                                : <span>{p.initials}</span>
                                            }
                                        </div>
                                        <div className="gal-photog-name">{p.name}</div>
                                        <div className="gal-photog-title">{p.title}</div>
                                        <div className="gal-photog-divider" />
                                        <div className="gal-photog-links">
                                            {p.ig
                                                ? <a href={`https://instagram.com/${p.ig.slice(1)}`} className="gal-pg-icon gal-pg-icon--ig" target="_blank" rel="noopener noreferrer" title={p.ig}><IgIcon /></a>
                                                : <span className="gal-pg-icon gal-pg-icon--off"><IgIcon /></span>
                                            }
                                            {p.email
                                                ? <a href={`mailto:${p.email}`} className="gal-pg-icon gal-pg-icon--mail" title={p.email}><MailIcon /></a>
                                                : <span className="gal-pg-icon gal-pg-icon--off"><MailIcon /></span>
                                            }
                                            {p.phone
                                                ? <a href={`tel:${p.phone.replace(/\s/g, '')}`} className="gal-pg-icon gal-pg-icon--phone" title={p.phone}><PhoneIcon /></a>
                                                : <span className="gal-pg-icon gal-pg-icon--off"><PhoneIcon /></span>
                                            }
                                            {p.facebook
                                                ? <a href={p.facebook} className="gal-pg-icon gal-pg-icon--fb" target="_blank" rel="noopener noreferrer" title="Facebook"><FbIcon /></a>
                                                : <span className="gal-pg-icon gal-pg-icon--off"><FbIcon /></span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            );
                            return (
                                <div className="gal-photog-marquee-wrap">
                                    <div className="gal-photog-track">
                                        {track1.map(renderCard)}
                                    </div>
                                    <div className="gal-photog-track gal-photog-track--rev">
                                        {track2.map(renderCard)}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* ── Section 2: Albums ── */}
                    <div id="gal-sec-albums" className="gallery-section gallery-section-albums">
                        <div className="container">
                            <div className="entry-title">
                                <p>BỘ SƯU TẬP</p>
                                <h2>ALBUM ẢNH LỄ HỘI</h2>
                            </div>

                            {/* Filter bar */}
                            <div className="gal-filter-bar">
                                <div className="gal-filter-select-wrap">
                                    <label className="gal-filter-label" htmlFor="filter-year">Năm</label>
                                    <div className="gal-select-box">
                                        <select
                                            id="filter-year"
                                            value={filterYear}
                                            onChange={e => this.setState({ filterYear: e.target.value })}
                                        >
                                            <option value="all">Tất cả</option>
                                            {Array.from({ length: 12 }, (_, i) => 2026 - i).map(y => (
                                                <option key={y} value={String(y)}>{y}</option>
                                            ))}
                                        </select>
                                        <span className="gal-select-arrow" />
                                    </div>
                                </div>

                                <div className="gal-filter-select-wrap">
                                    <label className="gal-filter-label">Sự kiện</label>
                                    <SearchableSelect
                                        value={filterFest}
                                        onChange={v => this.setState({ filterFest: v })}
                                        options={festivals.map(f => ({ value: String(f.id), label: f.name }))}
                                        placeholder="Tất cả sự kiện"
                                        loading={festivalsLoading}
                                    />
                                </div>

                                <div className="gal-filter-select-wrap">
                                    <label className="gal-filter-label">Nhiếp ảnh</label>
                                    <SearchableSelect
                                        value={filterCameraman}
                                        onChange={v => this.setState({ filterCameraman: v })}
                                        options={cameramen.map(c => ({ value: String(c.id), label: c.name }))}
                                        placeholder="Tất cả nháy"
                                        loading={cameramenLoading}
                                    />
                                </div>

                                

                                {(filterFest !== 'all' || filterCameraman !== 'all' || filterTeam !== 'all') && (
                                    <button
                                        className="gal-filter-reset"
                                        onClick={() => this.setState({ filterFest: 'all', filterCameraman: 'all', filterTeam: 'all' })}
                                    >✕ Xóa bộ lọc</button>
                                )}
                            </div>

                            <div className="gal-filter-count">
                                {albumsLoading
                                    ? 'Đang tải album...'
                                    : <>Hiển thị <strong>{filtered.length}</strong> / {albums.length} album</>
                                }
                            </div>

                            {albumsLoading ? (
                                <SakuraOrbit label="Đang tải album" />
                            ) : filtered.length > 0 ? (
                                <>
                                <div className="gal-albums-grid">
                                    {pagedAlbums.map(a => {
                                        const festName = (festivals.find(f => String(f.id) === String(a.festival_id)) || {}).name || '—';
                                        const camName  = cameramenById[a.cameraman_id] || '—';
                                        return (
                                            <Link key={a.id} to={`/gallery/album/${a.id}`} className="gal-album-card">
                                                <div className="gal-album-img-wrap">
                                                    <img src={s3(a.cover_image_url, 'images/next-event-1.jpg')} alt={a.title} />
                                                    <div className="gal-album-overlay" />
                                                    {a.description && (
                                                        <div className="gal-album-top-badge">
                                                            <CameraIcon />
                                                            <span>{a.description}</span>
                                                        </div>
                                                    )}
                                                    {a.pic_number > 0 && (
                                                        <div className="gal-album-pic-count">
                                                            <CameraIcon />
                                                            {a.pic_number}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="gal-album-body">
                                                    <h3 className="gal-album-name">{a.title}</h3>
                                                    <div className="gal-album-rows">
                                                        <div className="gal-album-row">
                                                            <span className="gal-album-key">Ngày tạo</span>
                                                            <span className="gal-album-val">{fmtDate(a.created_at)}</span>
                                                        </div>
                                                        <div className="gal-album-row">
                                                            <span className="gal-album-key">Sự kiện</span>
                                                            <span className="gal-album-val">{festName}</span>
                                                        </div>
                                                        {a.cameraman_id && (
                                                            <div className="gal-album-row">
                                                                <span className="gal-album-key">Nhiếp ảnh</span>
                                                                <span className="gal-album-val gal-album-phot">{camName}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>

                                {albumTotalPages > 1 && (
                                    <div className="gal-album-pager">
                                        <button
                                            className="gal-pager-arrow"
                                            disabled={albumPage === 1}
                                            onClick={() => this.setAlbumPage(albumPage - 1)}
                                            aria-label="Trang trước"
                                        >‹</button>
                                        {Array.from({ length: albumTotalPages }, (_, i) => i + 1).map(p => (
                                            <button
                                                key={p}
                                                className={`gal-pager-num${p === albumPage ? ' active' : ''}`}
                                                onClick={() => this.setAlbumPage(p)}
                                            >{p}</button>
                                        ))}
                                        <button
                                            className="gal-pager-arrow"
                                            disabled={albumPage === albumTotalPages}
                                            onClick={() => this.setAlbumPage(albumPage + 1)}
                                            aria-label="Trang sau"
                                        >›</button>
                                    </div>
                                )}
                                </>
                            ) : (
                                <div className="gal-no-results">
                                    <div className="gal-no-results-kanji">桜</div>
                                    <p>Không tìm thấy album phù hợp với bộ lọc</p>
                                    <button
                                        className="gal-filter-reset"
                                        onClick={() => this.setState({ filterFest: 'all', filterCameraman: 'all' })}
                                    >Xóa bộ lọc</button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Section 3: Team ranking by tagged pictures ── */}
                    <div id="gal-sec-ranking" className="gallery-section gallery-section-last">
                        <div className="container">
                            <div className="entry-title">
                                <p>Ranking of Ou</p>
                                <h2>XẾP HẠNG ẢNH ĐƯỢC GẮN TAG</h2>
                            </div>
                            {rankingLoading ? (
                                <SakuraOrbit size="sm" label="Đang tải" />
                            ) : teamRanking.length === 0 ? (
                                <div className="gal-no-results">
                                    <div className="gal-no-results-kanji">桜</div>
                                    <p>Chưa có ảnh nào được gắn tag</p>
                                </div>
                            ) : (
                                <div className="gal-dancer-list">
                                    {teamRanking.map((t) => {
                                        const pct       = maxPics > 0 ? Math.round((t.pic_count / maxPics) * 100) : 0;
                                        const rankStyle = RANK_STYLES[t.rank - 1] || null;
                                        const teamInits = (t.name || '').split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(-2).join('');
                                        const isTop     = t.rank <= 3 && t.pic_count > 0;
                                        return (
                                            <div key={t.id} className={`gal-dancer-row${t.rank <= 3 ? ' gal-dancer-top' : ''}`}>
                                                <div
                                                    className="gal-rank-badge"
                                                    style={rankStyle ? { background: rankStyle.bg, color: rankStyle.color } : {}}
                                                >
                                                    {t.rank}
                                                </div>

                                                <Link
                                                    to={`/teams/${t.slug || t.id}`}
                                                    className={`gal-dancer-avatar gal-dancer-avatar--link${isTop ? ` gal-aura gal-aura--${t.rank}` : ''}`}
                                                    style={{ background: grad(t.rank - 1) }}
                                                    title={`Xem trang đội ${t.name}`}
                                                >
                                                    {isTop && (
                                                        <span className="team-aura" aria-hidden="true">
                                                            <span className="team-aura-rays" />
                                                            <span className="team-aura-glow" />
                                                            <span className="team-aura-ring" />
                                                            <span className="team-aura-ring team-aura-ring--late" />
                                                            {[0, 1, 2, 3, 4].map(k => <span key={k} className={`team-aura-spark team-aura-spark--${k}`} />)}
                                                        </span>
                                                    )}
                                                    {t.avatar_url
                                                        ? <img src={s3(t.avatar_url)} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                                        : <span>{teamInits}</span>
                                                    }
                                                    {isTop && <Crown rank={t.rank} count={t.pic_count} />}
                                                </Link>

                                                <div className="gal-dancer-info">
                                                    <div className={`gal-dancer-name${isTop ? ` gal-aura-name gal-aura--${t.rank}` : ''}`}>{t.name}</div>
                                                    <div className="gal-dancer-team">{t.location === 'vn' ? 'Việt Nam' : t.location === 'jp' ? 'Nhật Bản' : (t.location || '').toUpperCase()}</div>
                                                </div>

                                                <div className="gal-dancer-stats">
                                                    <div className="gal-dancer-count">
                                                        {t.pic_count.toLocaleString('vi-VN')}
                                                        <span className="gal-dancer-unit"> ảnh</span>
                                                    </div>
                                                    <div className="gal-bar-track">
                                                        <div className="gal-bar-fill" style={{ width: `${pct}%` }} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    <SakuraSep />
                </div>

                <div className="sep-gold-rule" style={{margin: 0}} />

            </div>
        );
    }
}

export default Gallery;
